import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Constants } from '../../core/helpers/constants';

export type DisplayDensityMode = 'comfortable' | 'condensed';

@Injectable({ providedIn: 'root' })
export class DisplayDensityService {
  private readonly storageKey = Constants.LS_DENSITY_MODE;
  private readonly density$ = new BehaviorSubject<DisplayDensityMode>('comfortable');
  private initialized = false;

  watch(): Observable<DisplayDensityMode> {
    this.ensureInitialized();
    return this.density$.asObservable();
  }

  getSnapshot(): DisplayDensityMode {
    this.ensureInitialized();
    return this.density$.value;
  }

  initialize(): void {
    if (this.initialized) {
      this.applyDensity(this.density$.value);
      return;
    }

    const storedDensity = this.readStoredDensity();
    this.initialized = true;
    this.density$.next(storedDensity);
    this.applyDensity(storedDensity);
  }

  setDensity(density: DisplayDensityMode): void {
    this.ensureInitialized();
    if (this.density$.value === density) {
      this.applyDensity(density);
      return;
    }

    this.density$.next(density);
    this.persistDensity(density);
    this.applyDensity(density);
  }

  toggleDensity(): DisplayDensityMode {
    const nextDensity: DisplayDensityMode = this.getSnapshot() === 'condensed' ? 'comfortable' : 'condensed';
    this.setDensity(nextDensity);
    return nextDensity;
  }

  private ensureInitialized(): void {
    if (!this.initialized) {
      this.initialize();
    }
  }

  private readStoredDensity(): DisplayDensityMode {
    const stored = (localStorage.getItem(this.storageKey) || '').trim().toLowerCase();
    return stored === 'condensed' ? 'condensed' : 'comfortable';
  }

  private persistDensity(density: DisplayDensityMode): void {
    localStorage.setItem(this.storageKey, density);
  }

  private applyDensity(density: DisplayDensityMode): void {
    document.documentElement.setAttribute('data-density', density);
    document.body.setAttribute('data-density', density);
  }
}